import React from 'react';
import './TableActionBar.css';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { TableStatus } from '@/common/types/phenomTypes';

interface TableActionBarProps {
  onStartHand?: () => void;
  onRestartService?: () => void;
  onKillServer?: () => void;
  onRefreshCache?: () => void;
  onViewHandHistory?: () => void;
  tournamentUrl?: string;
  tableId?: string;
  isRefreshingCache?: boolean;
  disabled?: boolean;
  className?: string;
  // New: toggle active state
  isActive?: boolean;
  onToggleActive?: () => void;
  currentStatus?: TableStatus;
  statusOptions?: TableStatus[];
  onStatusChange?: (status: TableStatus) => void;
  isStatusUpdating?: boolean;
}

const TableActionBar: React.FC<TableActionBarProps> = ({
  onStartHand,
  onRestartService,
  onKillServer,
  onRefreshCache,
  onViewHandHistory,
  tournamentUrl,
  tableId,
  isRefreshingCache = false,
  disabled = false,
  className = '',
  isActive,
  onToggleActive,
  currentStatus,
  statusOptions,
  onStatusChange,
  isStatusUpdating = false,
}) => {
  const getButtonClass = (variant: string, extraClasses = '') => {
    return `action-button ${variant} ${extraClasses}`.trim();
  };

  const renderButton = (
    onClick: (() => void) | undefined,
    icon: string,
    text: string,
    variant: string,
    isLoading = false,
    condition = true,
    title?: string,
  ) => {
    if (!condition || !onClick) return null;

    return (
      <button
        onClick={onClick}
        disabled={disabled || isLoading}
        className={getButtonClass(variant)}
        title={title || text}
        aria-label={text}
      >
        {isLoading ? (
          <>
            <div className="spinner" aria-hidden="true"></div>
            <span className="button-text">Loading...</span>
          </>
        ) : (
          <>
            <i className={`${icon} action-icon`} aria-hidden="true"></i>
            <span className="button-text">{text}</span>
          </>
        )}
      </button>
    );
  };

  return (
    <div className={`table-action-bar ${className}`}>
      <div className="action-group primary-actions">
        {renderButton(
          onStartHand,
          'ri-play-circle-line',
          'Start Hand',
          'btn-warning',
          false,
          !!tournamentUrl,
          'Start a new hand for this tournament',
        )}

        {renderButton(
          onViewHandHistory,
          'ri-history-line',
          'Hand History',
          'btn-primary',
          false,
          true,
          'View hand history for this table',
        )}

        {renderButton(
          onRefreshCache,
          'ri-refresh-line',
          'Refresh Cache',
          'btn-info',
          isRefreshingCache,
          !!tournamentUrl,
          'Refresh the tournament cache',
        )}
      </div>

      <div className="action-divider"></div>

      <div className="action-group secondary-actions">
        {statusOptions && onStatusChange && statusOptions.length > 0 && (
          <UncontrolledDropdown className="status-dropdown-wrapper">
            <DropdownToggle
              caret
              disabled={disabled || isStatusUpdating}
              className={getButtonClass(
                currentStatus === TableStatus.RUNNING
                  ? 'btn-success'
                  : currentStatus === TableStatus.STOPPED
                    ? 'btn-danger'
                    : 'btn-secondary',
                'dropdown-toggle status-dropdown-toggle',
              )}
            >
              <span className="status-label">
                <span
                  className={`status-dot ${currentStatus ? `status-${currentStatus.toLowerCase()}` : 'status-unknown'}`}
                  aria-hidden="true"
                />
                <span className="status-text">{isStatusUpdating ? 'Updating...' : currentStatus || 'UNKNOWN'}</span>
              </span>
            </DropdownToggle>
            <DropdownMenu className="status-dropdown-menu">
              {statusOptions.map((option) => (
                <DropdownItem
                  key={option}
                  disabled={isStatusUpdating || option === currentStatus}
                  onClick={() => onStatusChange(option)}
                  className={`status-dropdown-item status-${option.toLowerCase()}${
                    option === currentStatus ? 'is-active' : ''
                  }`}
                >
                  <div className="status-option">
                    <span className={`status-dot status-${option.toLowerCase()}`} aria-hidden="true" />
                    <span className="status-option-text">{option}</span>
                    {option === currentStatus && <i className="ri-check-line status-option-check" aria-hidden="true" />}
                  </div>
                </DropdownItem>
              ))}
            </DropdownMenu>
          </UncontrolledDropdown>
        )}
        {renderButton(
          onToggleActive,
          'ri-toggle-line',
          isActive ? 'Active' : 'Inactive',
          isActive ? 'btn-success' : 'btn-secondary',
          false,
          true,
          isActive ? 'Set table inactive' : 'Set table active',
        )}
        {renderButton(
          onRestartService,
          'ri-restart-line',
          'Restart Service',
          'btn-secondary',
          false,
          true,
          'Restart the table service',
        )}

        {renderButton(
          onKillServer,
          'ri-delete-bin-line',
          'Kill Server',
          'btn-danger',
          false,
          true,
          'Kill the table server (destructive action)',
        )}
      </div>
    </div>
  );
};

export default TableActionBar;
